/* femme PDP bundle builder V1 | 2026-09-28 */
(() => {
  'use strict';

  const ROOT = '[data-femme-product-bundle]';
  const cartRoot = () => window.Shopify?.routes?.root || '/';

  const json = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.body ? { 'Content-Type': 'application/json' } : {})
      },
      ...options
    });
    const data = await response.json();
    if (!response.ok || data?.errors) {
      throw new Error(data?.description || data?.message || 'Unable to update the cart.');
    }
    return data;
  };

  const normaliseCadence = (name) => {
    const value = String(name || '').toLowerCase();
    if (/fortnight|every\s*2\s*weeks?/.test(value)) return '2-weeks';
    if (/3\s*monthly|every\s*3\s*months?/.test(value)) return '3-months';
    if (/2\s*monthly|every\s*2\s*months?/.test(value)) return '2-months';
    if (/1\s*monthly|every\s*month|monthly\s*subscription/.test(value)) return '1-month';
    return value.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const productJson = async (handle) => {
    const response = await fetch(`${cartRoot()}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      cache: 'force-cache',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load subscription options.');
    return response.json();
  };

  const planFromProduct = (product, variantId, wantedCadence) => {
    const groups = Array.isArray(product?.selling_plan_groups) ? product.selling_plan_groups : [];
    for (const group of groups) {
      for (const plan of group.selling_plans || []) {
        if (normaliseCadence(plan.name) !== wantedCadence) continue;
        const variant = (product.variants || []).find((entry) => String(entry.id) === String(variantId));
        const allocations = variant?.selling_plan_allocations || [];
        if (allocations.some((allocation) => String(allocation.selling_plan_id) === String(plan.id))) {
          return Number(plan.id);
        }
      }
    }
    return null;
  };

  const currentPurchaseState = async (form, handle, variantId) => {
    const appstle = window.FemmeAppstleMigration;
    const productRoot =
      form?.closest('product-info[data-product-id]') ||
      document.querySelector('product-info[id^="MainProduct-"]') ||
      document.querySelector('product-info[data-product-id]');

    const selectionType = String(
      productRoot?.dataset?.fscSelection ||
      appstle?.state?.selectionType ||
      ''
    ).toLowerCase();

    if (selectionType === 'one_time' || selectionType === 'one-time' || selectionType === 'onetime' || selectionType === 'once') {
      return { type: 'one-time', cadence: '' };
    }

    if (selectionType === 'subscription') {
      const planId = String(
        productRoot?.dataset?.fscSelectedPlanId ||
        appstle?.state?.selectedPlanId ||
        ''
      );
      if (!planId) return { type: 'one-time', cadence: '' };

      const label =
        appstle?.PLAN_LABELS?.[planId] ||
        appstle?.state?.variants
          ?.get?.(String(variantId))
          ?.plans?.find?.((plan) => String(plan.id) === planId)
          ?.name ||
        '';

      if (label) {
        return { type: 'subscription', cadence: normaliseCadence(label) };
      }

      try {
        const product = await productJson(handle);
        for (const group of product?.selling_plan_groups || []) {
          const plan = (group.selling_plans || []).find((entry) => String(entry.id) === planId);
          if (plan) return { type: 'subscription', cadence: normaliseCadence(plan.name) };
        }
      } catch (error) {
        console.warn('[femme bundle] Unable to resolve Appstle cadence.', error);
      }

      return { type: 'one-time', cadence: '' };
    }

    /* Fallback for product templates without the femme Appstle runtime. */
    const data = new FormData(form);
    const planId = String(data.get('selling_plan') || '');
    if (!planId) return { type: 'one-time', cadence: '' };

    try {
      const product = await productJson(handle);
      for (const group of product?.selling_plan_groups || []) {
        const plan = (group.selling_plans || []).find((entry) => String(entry.id) === planId);
        if (plan) return { type: 'subscription', cadence: normaliseCadence(plan.name) };
      }
    } catch (error) {
      console.warn('[femme bundle] Unable to resolve current cadence.', error);
    }

    return { type: 'one-time', cadence: '' };
  };

  const publishCart = (cart, source = 'femme-product-bundle') => {
    if (window.FoxTheme?.pubsub?.publish && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        sources: source,
        cart
      });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { source, cart }
    }));
  };

  const init = (root) => {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const dialog = document.getElementById(root.dataset.pickerId);
    const status = root.querySelector('[data-femme-bundle-status]');
    const search = dialog?.querySelector('[data-femme-bundle-search]');
    let pending = [];
    let armed = false;
    let adding = false;

    const slots = () => Array.from(root.querySelectorAll('[data-femme-bundle-slot]'));
    const productIds = () => new Set(slots().map((slot) => String(slot.dataset.productId || '')));

    const setStatus = (message, error = false) => {
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('is-error', error);
    };

    const syncPicker = () => {
      const selected = productIds();
      dialog?.querySelectorAll('[data-femme-bundle-product]').forEach((card) => {
        const button = card.querySelector('[data-femme-bundle-select]');
        const occupied = selected.has(String(card.dataset.productId || ''));
        if (!button) return;
        button.disabled = occupied;
        button.textContent = occupied ? 'Already in bundle' : 'Add to bundle';

        const existingRemove = card.querySelector('[data-femme-bundle-picker-remove]');
        if (occupied) {
          const pendingSlot = slots().find((slot) =>
            String(slot.dataset.productId || '') === String(card.dataset.productId || '') &&
            String(slot.dataset.source || '') === 'pending'
          );
          if (pendingSlot && !existingRemove) {
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'femme-product-bundle-picker__remove';
            remove.dataset.femmeBundlePickerRemove = '';
            remove.setAttribute('aria-label', `Remove ${card.dataset.title || 'product'} from bundle`);
            remove.textContent = '×';
            card.appendChild(remove);
          }
        } else {
          existingRemove?.remove();
        }
      });
    };

    const selectedBundleItems = () => slots()
      /* Slot 1 is always the PDP product. Only slots 2/3 are bundle additions.
         Do not infer this from cart state: cart membership can change after the
         builder has been rendered. */
      .filter((slot) => String(slot.dataset.source || '') !== 'current')
      .map((slot) => ({
        productId: String(slot.dataset.productId || ''),
        variantId: String(slot.dataset.variantId || ''),
        handle: String(slot.dataset.handle || '')
      }))
      .filter((item) => item.productId && item.variantId && item.handle);

    const emptyButton = () => root.querySelector('[data-femme-bundle-empty]');

    const makePendingSlot = (card, variantId) => {
      const media = card.querySelector('.femme-product-bundle-picker__media img');
      const wrap = document.createElement('div');
      wrap.className = 'femme-product-bundle__slot-wrap is-filled is-pending';
      wrap.dataset.femmeBundleSlot = '';
      wrap.dataset.source = 'pending';
      wrap.dataset.productId = card.dataset.productId || '';
      wrap.dataset.variantId = String(variantId || '');
      wrap.dataset.handle = card.dataset.handle || '';

      const slot = document.createElement('div');
      slot.className = 'femme-product-bundle__slot is-filled';
      if (media) {
        const image = media.cloneNode(true);
        image.removeAttribute('loading');
        slot.appendChild(image);
      }

      const badge = document.createElement('span');
      badge.className = 'femme-product-bundle__badge';
      badge.textContent = 'Added with this product';
      slot.appendChild(badge);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'femme-product-bundle__remove';
      remove.dataset.femmeBundleRemove = '';
      remove.setAttribute('aria-label', `Remove ${card.dataset.title || 'product'} from bundle`);
      remove.textContent = '×';

      wrap.append(slot, remove);
      return wrap;
    };

    const restoreEmpty = () => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'femme-product-bundle__slot-wrap is-empty';
      button.dataset.femmeBundleEmpty = '';
      button.setAttribute('aria-controls', root.dataset.pickerId);
      button.setAttribute('aria-label', 'Choose another eligible product');
      button.innerHTML = '<span class="femme-product-bundle__slot is-empty" aria-hidden="true"><svg width="30" height="30" viewBox="0 0 30 30" fill="none"><path d="M15 5V25M5 15H25" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></span>';
      root.querySelector('[data-femme-bundle-slots]')?.appendChild(button);
    };

    const closeProductInfo = (popup, afterClose) => {
      if (!popup?.open || popup.classList.contains('is-bundle-closing')) return;
      popup.classList.add('is-bundle-closing');
      window.setTimeout(() => {
        popup.close();
        popup.classList.remove('is-bundle-closing');
        if (typeof afterClose === 'function') afterClose();
      }, 220);
    };

    const openProductInfo = (trigger) => {
      const popupId = trigger?.getAttribute('data-femme-bundle-info-open');
      const popup = popupId ? document.getElementById(popupId) : null;
      if (!popup) return;
      if (typeof popup.showModal === 'function') popup.showModal();
      else popup.setAttribute('open', '');
    };

    root.addEventListener('keydown', (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-femme-bundle-info-open]')) {
        event.preventDefault();
        openProductInfo(event.target);
      }
    });

    root.addEventListener('click', (event) => {
      const popupChoose = event.target.closest('[data-femme-bundle-popup-choose]');
      if (popupChoose) {
        event.preventDefault();
        event.stopPropagation();
        const popup = popupChoose.closest('dialog');
        popup?.querySelector('[data-femme-quick-options-choices]')?.removeAttribute('hidden');
        popupChoose.hidden = true;
        return;
      }

      const popupAdd = event.target.closest('[data-femme-bundle-popup-add]');
      if (popupAdd) {
        event.preventDefault();
        event.stopPropagation();
        const popup = popupAdd.closest('dialog');
        const card = popup?.closest('[data-femme-bundle-product]');
        const target = emptyButton();
        if (!card || !target) return;

        const variantSelect = popup.querySelector('[data-femme-quick-options-variant]');
        const variantId = String(variantSelect?.value || card.querySelector('[data-femme-bundle-variant]')?.value || '');
        if (!variantId) {
          variantSelect?.focus();
          return;
        }

        const productId = String(card.dataset.productId || '');
        if (!productId || productIds().has(productId)) {
          closeProductInfo(popup);
          return;
        }

        const cardVariant = card.querySelector('[data-femme-bundle-variant]');
        if (cardVariant) cardVariant.value = variantId;

        pending.push({ productId, variantId, handle: card.dataset.handle || '' });
        target.replaceWith(makePendingSlot(card, variantId));
        syncPicker();
        setStatus('');
        closeProductInfo(popup, () => {
          if (!emptyButton()) dialog.close();
        });
        return;
      }

            const popupClose = event.target.closest('[data-femme-quick-options-close]');
      if (popupClose) {
        event.preventDefault();
        event.stopPropagation();
        const popup = popupClose.closest('dialog');
        closeProductInfo(popup);
        return;
      }

      const infoTrigger = event.target.closest('[data-femme-bundle-info-open]');
      if (infoTrigger) {
        event.preventDefault();
        event.stopPropagation();
        openProductInfo(infoTrigger);
        return;
      }

      const open = event.target.closest('[data-femme-bundle-empty]');
      if (open && dialog) {
        syncPicker();
        dialog.showModal();
        window.requestAnimationFrame(() => search?.focus());
        return;
      }

      const remove = event.target.closest('[data-femme-bundle-remove]');
      if (remove) {
        const wrap = remove.closest('[data-femme-bundle-slot]');
        const id = String(wrap?.dataset.productId || '');
        pending = pending.filter((item) => item.productId !== id);
        wrap?.remove();
        restoreEmpty();
        syncPicker();
        setStatus('');
      }
    });

    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-femme-bundle-close]')) {
        dialog.close();
        return;
      }

      const pickerRemove = event.target.closest('[data-femme-bundle-picker-remove]');
      if (pickerRemove) {
        event.preventDefault();
        event.stopPropagation();
        const card = pickerRemove.closest('[data-femme-bundle-product]');
        const productId = String(card?.dataset.productId || '');
        const wrap = slots().find((slot) =>
          String(slot.dataset.productId || '') === productId &&
          String(slot.dataset.source || '') === 'pending'
        );
        if (!wrap) return;
        pending = pending.filter((item) => item.productId !== productId);
        wrap.remove();
        restoreEmpty();
        syncPicker();
        setStatus('');
        return;
      }

            const variantOption = event.target.closest('[data-femme-bundle-variant-option]');
      if (variantOption) {
        const card = variantOption.closest('[data-femme-bundle-product]');
        const input = card?.querySelector('[data-femme-bundle-variant]');
        card?.querySelectorAll('[data-femme-bundle-variant-option]').forEach((option) => {
          const selected = option === variantOption;
          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        if (input) input.value = variantOption.dataset.variantId || '';
        return;
      }

      const select = event.target.closest('[data-femme-bundle-select]');
      if (!select) return;

      const card = select.closest('[data-femme-bundle-product]');
      const variantControl = card?.querySelector('[data-femme-bundle-variant]');
      const variantId = String(variantControl?.value || '');
      const productId = String(card?.dataset.productId || '');

      if (!card || !variantId || !productId) return;

      const target = emptyButton();
      if (!target || productIds().has(productId)) return;

      pending.push({
        productId,
        variantId,
        handle: card.dataset.handle || ''
      });
      target.replaceWith(makePendingSlot(card, variantId));
      syncPicker();
      setStatus('');

      if (!emptyButton()) dialog.close();
    });

    search?.addEventListener('input', () => {
      const query = String(search.value || '').trim().toLowerCase();
      let visible = 0;
      dialog.querySelectorAll('[data-femme-bundle-product]').forEach((card) => {
        const show = !query || String(card.dataset.search || '').includes(query);
        card.hidden = !show;
        if (show) visible += 1;
      });
      const empty = dialog.querySelector('[data-femme-bundle-no-results]');
      if (empty) empty.hidden = visible > 0;
    });

    document.addEventListener('click', (event) => {
      const add = event.target.closest('button[name="add"], .femme-add-to-cart-button');
      const selected = selectedBundleItems();
      if (!add || selected.length === 0) return;
      const form = add.closest('form[data-type="add-to-cart-form"], form.main-product-form');
      if (!form) return;

      armed = true;
      root._femmeBundleForm = form;
      setStatus('Your selected bundle products will be added with this item.');

      /* Start resolving the bundle payload immediately, in parallel with the
         theme's native main-product add. This removes the avoidable wait for
         product JSON/cart reads after the first cart update arrives. */
      const currentVariant = String(
        form.querySelector('[name="id"]')?.value ||
        root.dataset.currentVariantId ||
        ''
      );
      root._femmeBundlePreparation = (async () => {
        const purchaseState = await currentPurchaseState(
          form,
          root.dataset.currentHandle,
          currentVariant
        );

        const items = await Promise.all(
          selected.map(async (selection) => {
            const item = { id: Number(selection.variantId), quantity: 1 };

            if (purchaseState.type === 'subscription' && purchaseState.cadence) {
              const targetProduct = await productJson(selection.handle);
              const targetPlan = planFromProduct(
                targetProduct,
                selection.variantId,
                purchaseState.cadence
              );
              if (targetPlan) item.selling_plan = targetPlan;
            }

            return item;
          })
        );

        return { items, selected: selected.slice() };
      })();
    }, true);

    const completePendingBundle = async (event = {}) => {
      if (!armed || adding) return;
      const source = event?.source || event?.detail?.source || '';
      if (source === 'femme-product-bundle') return;

      armed = false;
      adding = true;
      const form = root._femmeBundleForm;
      const selected = selectedBundleItems();
      if (selected.length === 0) {
        armed = false;
        return;
      }

      try {
        let prepared = root._femmeBundlePreparation
          ? await root._femmeBundlePreparation
          : null;
        root._femmeBundlePreparation = null;

        if (!prepared) {
          const currentVariant = String(
            form?.querySelector('[name="id"]')?.value ||
            root.dataset.currentVariantId ||
            ''
          );
          const purchaseState = form
            ? await currentPurchaseState(form, root.dataset.currentHandle, currentVariant)
            : { type: 'one-time', cadence: '' };

          const preparedItems = await Promise.all(
            selected.map(async (selection) => {
              const item = { id: Number(selection.variantId), quantity: 1 };
              if (purchaseState.type === 'subscription' && purchaseState.cadence) {
                const targetProduct = await productJson(selection.handle);
                const targetPlan = planFromProduct(
                  targetProduct,
                  selection.variantId,
                  purchaseState.cadence
                );
                if (targetPlan) item.selling_plan = targetPlan;
              }
              return item;
            })
          );
          prepared = { items: preparedItems, selected };
        }

        const eventCart =
          event?.cart ||
          event?.detail?.cart ||
          event?.data?.cart ||
          null;
        const existingVariantIds = new Set(
          (eventCart?.items || []).map((item) => String(item.variant_id))
        );

        const items = prepared.items.filter(
          (item) => !existingVariantIds.has(String(item.id))
        );

        if (!items.length) {
          setStatus('Your bundle is already in the cart.');
          return;
        }

        await json(`${cartRoot()}cart/add.js`, {
          method: 'POST',
          body: JSON.stringify({ items })
        });

        const updatedCart = await json(`${cartRoot()}cart.js`);

        /* The theme cart renderer expects section HTML on cart-update payloads.
           A plain /cart.js object has no sections map and can make removals
           throw while trying to read the cart-drawer section. Let the femme
           cart synchroniser perform its normal rendered-section refresh instead. */
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'femme-product-bundle', cart: updatedCart }
        }));
        /* Keep the builder selection intact. Cart membership is transient;
           the selected slots are the durable customer choice and are reused
           if the cart is later cleared. */
        pending = selected.slice();
        setStatus('Bundle products added to your cart.');
      } catch (error) {
        console.error('[femme bundle]', error);
        setStatus(error?.message || 'Unable to add the bundle products. Please try again.', true);
      } finally {
        adding = false;
      }
    };

    if (window.FoxTheme?.pubsub?.subscribe && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      window.FoxTheme.pubsub.subscribe(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        completePendingBundle
      );
    } else {
      document.addEventListener('product-ajax:added', completePendingBundle);
      document.addEventListener('cart:updated', completePendingBundle);
    }

    syncPicker();
  };

  const boot = () => document.querySelectorAll(ROOT).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
